import { isBookingRatePendingCommissionPlaceholder } from '../Booking/getNewIndependentBookingPlans'

export interface IGroupedBookingPlanRateRow {
  key: string
  label: string
  planIds: string[]
  percent: string | number | undefined
}

export interface INamedBookingPlan {
  booking_id: string | number
  name?: string
}

export interface IGroupedNamedBookingPlan<T extends INamedBookingPlan> {
  key: string
  label: string
  plans: T[]
  planIds: string[]
}

/** Klucz grupowania / filtra nazwy planu — bez manipulacji (case-sensitive). */
export function getBookingPlanNameGroupKey(
  name: string | undefined | null,
): string {
  return String(name || '')
}

function groupByLabel<T>(
  items: T[],
  getLabel: (item: T) => string,
): Map<string, { labels: string[]; items: T[] }> {
  const groups = new Map<string, { labels: string[]; items: T[] }>()
  for (const item of items) {
    const label = getLabel(item)
    const key = getBookingPlanNameGroupKey(label)
    const group = groups.get(key) ?? { labels: [], items: [] }
    group.labels.push(label)
    group.items.push(item)
    groups.set(key, group)
  }
  return groups
}

export function groupBookingPlanRatesByName(
  rates: Record<string, unknown>,
  ratesNames?: Record<string, string> | null,
): IGroupedBookingPlanRateRow[] {
  if (!rates || typeof rates !== 'object') return []
  const names = ratesNames || {}

  const groups = groupByLabel(Object.keys(rates), planId => {
    const raw = String(names[planId] || '')
    return raw || planId
  })

  return Array.from(groups.entries())
    .map(([key, group]) => ({
      key,
      label: group.labels[0] || group.items[0] || key,
      planIds: group.items,
      percent: rates[group.items[0]] as string | number | undefined,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** Grupuje plany po dokładnej nazwie (case-sensitive) — modale prowizji na cenniku. */
export function groupNamedBookingPlans<T extends INamedBookingPlan>(
  plans: T[],
  resolveLabel?: (plan: T) => string,
): IGroupedNamedBookingPlan<T>[] {
  const getLabel =
    resolveLabel ??
    (plan => String(plan.name || '') || String(plan.booking_id))

  const groups = groupByLabel(plans, getLabel)

  return Array.from(groups.entries())
    .map(([key, group]) => ({
      key,
      label: group.labels[0] || String(group.items[0]?.booking_id ?? key),
      plans: group.items,
      planIds: group.items.map(p => String(p.booking_id)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export function getGroupedBookingPlanRateValue(
  planIds: string[],
  formRates: Record<string, number | string>,
  fallbackRates?: Record<string, unknown>,
): number | string {
  for (const planId of planIds) {
    if (planId in formRates) return formRates[planId]
  }
  if (fallbackRates) {
    for (const planId of planIds) {
      const v = fallbackRates[planId]
      if (v !== undefined) return v as number | string
    }
  }
  return 0
}

/** Puste pole, przecinek/kropka → 0 przed zapisem do API (BED-40008). */
export function normalizeBookingPlanRateValue(value: unknown): number {
  if (value === '' || value === undefined || value === null) return 0
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(',', '.')
    if (trimmed === '' || trimmed === '.') return 0
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : 0
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/** Format prowizji do listy „Ceny wysyłane do Booking.com” (np. +15,5%). */
export function formatBookingPlanCommissionPercent(
  value: unknown,
): string {
  const n = normalizeBookingPlanRateValue(value)
  const sign = n > 0 ? '+' : ''
  const abs = Math.abs(n)
  const formatted = Number.isInteger(abs)
    ? String(abs)
    : String(abs).replace('.', ',')
  return `${sign}${formatted}%`
}

/** Czy użytkownik wpisał prowizję (0 jest OK, puste pole — nie). */
export function isBookingPlanRateFilled(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(',', '.')
    if (trimmed === '' || trimmed === '.') return false
    return Number.isFinite(Number(trimmed))
  }
  if (typeof value === 'number') return Number.isFinite(value)
  return false
}

export function areAllBookingPlanRatesFilled(
  planIdsGroups: string[][],
  planRates: Record<string, number | string>,
): boolean {
  if (planIdsGroups.length === 0) return true
  return planIdsGroups.every(planIds =>
    isBookingPlanRateFilled(
      getGroupedBookingPlanRateValue(planIds, planRates),
    ),
  )
}

export function normalizeBookingPlanRatesForSave(
  rates: Record<string, unknown> | null | undefined,
): Record<string, number | string> {
  if (!rates || typeof rates !== 'object') return {}
  const out: Record<string, number | string> = {}
  for (const [planId, rate] of Object.entries(rates)) {
    if (isBookingRatePendingCommissionPlaceholder(rate)) {
      out[planId] = 0
      continue
    }
    const n = normalizeBookingPlanRateValue(rate)
    // Jawne 0% z modala — string "0", nie placeholder liczbowy.
    out[planId] = n === 0 ? '0' : n
  }
  return out
}

/** Po zapisie / przeładowaniu configu — pokaż 0 z API zamiast pustego inputa. */
export function mergeBookingPlanRatesWithConfig(
  formRates: Record<string, number | string>,
  configRates: Record<string, unknown>,
): Record<string, number | string> {
  const next: Record<string, number | string> = {}
  const allKeys = new Set([
    ...Object.keys(configRates),
    ...Object.keys(formRates),
  ])
  for (const planId of allKeys) {
    const local = formRates[planId]
    const config = configRates[planId]
    if (
      local !== undefined &&
      normalizeBookingPlanRateValue(local) !==
        normalizeBookingPlanRateValue(config)
    ) {
      next[planId] = local
    } else if (config !== undefined) {
      next[planId] = normalizeBookingPlanRateValue(config)
    } else if (local !== undefined) {
      next[planId] = normalizeBookingPlanRateValue(local)
    }
  }
  return next
}

export function resolvePercentFieldFromConfig(
  local: unknown,
  config: unknown,
): number | string {
  if (
    normalizeBookingPlanRateValue(local) ===
      normalizeBookingPlanRateValue(config) &&
    local !== config
  ) {
    return normalizeBookingPlanRateValue(config)
  }
  if (typeof local === 'string') return local
  if (typeof local === 'number') return local
  return normalizeBookingPlanRateValue(config)
}

export function applyBookingRateToPlanGroup<V extends number | string>(
  rates: Record<string, V>,
  planIds: string[],
  value: V,
): Record<string, V> {
  const next = { ...rates }
  for (const planId of planIds) {
    next[planId] = value
  }
  return next
}
